import { Avatar, Button, FlexBox, Tooltip } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { useToggle } from 'react-use';
import { MarkRequired } from 'ts-essentials';

import { CustomFieldChip } from 'components/customFields';
import { useCustomFields } from 'utils/customFields';

import styles from './conversationPreviewCustomFields.module.scss';

type ConversationPreviewCustomFieldsProps = {
  csid: number;
  scrollable?: boolean;
  className?: string;
  showSectionIcon?: boolean;
};

const initiallyVisibleItemCount = 10;

export const ConversationPreviewCustomFields = ({
  csid,
  scrollable = false,
  className,
  showSectionIcon,
}: ConversationPreviewCustomFieldsProps): JSX.Element | null => {
  const { customFields } = useCustomFields({
    entityId: 'conversation',
    entityInstanceId: `${csid}`,
  });
  const itemsWithValue = customFields.filter(
    (customField) => customField.value !== undefined,
  ) as readonly MarkRequired<(typeof customFields)[number], 'value'>[];
  const showToggleButton = initiallyVisibleItemCount < itemsWithValue.length;

  const [showAllItems, toggleShowAllItems] = useToggle(false);

  const renderedItems = itemsWithValue.slice(
    0,
    showAllItems ? itemsWithValue.length : initiallyVisibleItemCount,
  );
  const unrenderedItemsLength = itemsWithValue.length - renderedItems.length;
  const rootClass = classnames(
    styles.root,
    {
      [styles.rootScrollable]: scrollable,
    },
    className,
  );

  if (!itemsWithValue.length) {
    return null;
  }

  return (
    <FlexBox
      className={rootClass}
      flexDirection="row"
      gap="200"
      alignItems="flex-start"
    >
      {showSectionIcon && (
        <Tooltip content="Conversation Attributes">
          <Avatar
            className={styles.icon}
            icon="input"
            color={{
              contentColor: colors.neutral[700],
              backgroundColor: colors.neutral[100],
            }}
          />
        </Tooltip>
      )}

      <FlexBox
        className={styles.customer}
        flexWrap={scrollable ? 'nowrap' : 'wrap'}
      >
        {renderedItems.map((customField) => {
          return (
            <CustomFieldChip
              key={customField.attribute.id}
              className={styles.customFieldChip}
              customField={customField}
            />
          );
        })}
      </FlexBox>

      {showToggleButton && (
        <Button variant="tertiary" onClick={toggleShowAllItems}>
          {showAllItems ? `Show less` : `+ ${unrenderedItemsLength} more`}
        </Button>
      )}
    </FlexBox>
  );
};
